/**
 * Gift Rewards API Endpoints
 * API service for the rewards and gifting system
 */

import { ApiService } from '../apiService'
import type {
  GiftRewardTemplateDto,
  GiftRewardTemplatePagedResult,
  GiftRewardInstanceDto,
  UserRewardDto,
  UserRewardPagedResult,
  TemplateStatsDto,
  RewardRedemptionLogPagedResult,
  RewardTransferHistoryPagedResult,
  RewardQrCodeDto,
  ValidateRewardCodeResponse,
  RedeemRewardRequest,
  RedeemRewardResponse,
  TransferRewardResponse,
  CanTransferResponse,
  CreateGiftRewardTemplateRequest,
  UpdateGiftRewardTemplateRequest,
  DistributeRewardRequest,
  DistributeRewardResponse,
  RewardInstanceStatus,
} from '../types/rewards'

export const rewardsApi = {
  // ===================
  // TEMPLATES (Group-based Management)
  // ===================

  /** Get all templates for a group */
  getGroupTemplates: (groupId: string, page: number = 1, pageSize: number = 20) =>
    ApiService.get<GiftRewardTemplatePagedResult>(
      `/api/groups/${groupId}/gift-rewards/templates`,
      { page, pageSize }
    ),

  /** Get single template */
  getTemplate: (groupId: string, templateId: string) =>
    ApiService.get<GiftRewardTemplateDto>(
      `/api/groups/${groupId}/gift-rewards/templates/${templateId}`
    ),

  /** Create template */
  createTemplate: (groupId: string, data: CreateGiftRewardTemplateRequest) =>
    ApiService.post<GiftRewardTemplateDto>(
      `/api/groups/${groupId}/gift-rewards/templates`,
      data
    ),

  /** Update template */
  updateTemplate: (groupId: string, templateId: string, data: UpdateGiftRewardTemplateRequest) =>
    ApiService.put<GiftRewardTemplateDto>(
      `/api/groups/${groupId}/gift-rewards/templates/${templateId}`,
      data
    ),

  /** Delete template */
  deleteTemplate: (groupId: string, templateId: string) =>
    ApiService.delete<void>(`/api/groups/${groupId}/gift-rewards/templates/${templateId}`),

  /** Toggle template active status */
  toggleTemplateActive: (groupId: string, templateId: string) =>
    ApiService.post<GiftRewardTemplateDto>(
      `/api/groups/${groupId}/gift-rewards/templates/${templateId}/toggle-active`
    ),

  /** Get template stats */
  getTemplateStats: (groupId: string, templateId: string) =>
    ApiService.get<TemplateStatsDto>(
      `/api/groups/${groupId}/gift-rewards/templates/${templateId}/stats`
    ),

  /** Get redemption history for template */
  getRedemptionHistory: (groupId: string, templateId: string, page: number = 1, pageSize: number = 20) =>
    ApiService.get<RewardRedemptionLogPagedResult>(
      `/api/groups/${groupId}/gift-rewards/templates/${templateId}/redemption-history`,
      { page, pageSize }
    ),

  /** Get transfer history for template */
  getTransferHistory: (groupId: string, templateId: string, page: number = 1, pageSize: number = 20) =>
    ApiService.get<RewardTransferHistoryPagedResult>(
      `/api/groups/${groupId}/gift-rewards/templates/${templateId}/transfer-history`,
      { page, pageSize }
    ),

  /** Distribute rewards to users */
  distributeRewards: (groupId: string, templateId: string, data: DistributeRewardRequest) =>
    ApiService.post<DistributeRewardResponse>(
      `/api/groups/${groupId}/gift-rewards/templates/${templateId}/distribute`,
      data
    ),

  // ===================
  // USER REWARDS
  // ===================

  /** Get current user's rewards (with optional status filter) */
  getMyRewards: (params?: { status?: RewardInstanceStatus; page?: number; pageSize?: number }) =>
    ApiService.get<UserRewardPagedResult>('/api/me/gift-rewards', params),

  /** Get single reward instance */
  getRewardInstance: (instanceId: string) =>
    ApiService.get<GiftRewardInstanceDto>(`/api/me/gift-rewards/${instanceId}`),

  /** Get QR code data for reward */
  getRewardQR: (instanceId: string) =>
    ApiService.get<RewardQrCodeDto>(`/api/me/gift-rewards/${instanceId}/qr`),

  /** Check if reward can be transferred */
  canTransferReward: (instanceId: string) =>
    ApiService.get<CanTransferResponse>(`/api/me/gift-rewards/${instanceId}/can-transfer`),

  /** Transfer reward to another user */
  transferReward: (instanceId: string, toUserId: string) =>
    ApiService.post<TransferRewardResponse>(`/api/me/gift-rewards/${instanceId}/transfer`, {
      toUserId,
    }),

  // ===================
  // REDEMPTION (Scanner Operations)
  // ===================

  /** Validate a reward code (called from scanner) */
  validateRewardCode: (code: string) =>
    ApiService.get<ValidateRewardCodeResponse>(`/api/gift-rewards/validate/${encodeURIComponent(code)}`),

  /** Redeem a reward */
  redeemReward: (data: RedeemRewardRequest) =>
    ApiService.post<RedeemRewardResponse>('/api/gift-rewards/redeem', data),
}
